<script>
  import { getContext, onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { link, useNavigate, useParams } from 'svelte-navigator';
  import { Breadcrumb, BreadcrumbItem, Button, Col, Container, Row } from '@sveltestrap/sveltestrap';

  import { Step } from '@client/components/steps';

  import { workflowsStore } from '@client/stores/workflows';

  import { APITransport } from '@client/transports/api';
  import { getWorkflowActionByValue } from '../workflow-steps-config';
  import { ReviewStep, StepContent } from './components';

  const { basepath } = getContext('config');
  const params = useParams();
  const navigate = useNavigate();

  $: workflowName = $params.id;
  $: workflow = $workflowsStore.find((w) => w.name === workflowName);

  let steps = [];
  let activeStep = 0;
  let stepNextDisabled = [];
  let stepExecuteFunctions = {};
  let stepRollbackFunctions = {};
  let stepExecutionStatus = {};
  let stepExecutionTimes = {};
  let stepExecutionErrors = {};
  let stepRequestLogs = {};

  onMount(() => {
    if (workflow?.steps) {
      steps = [
        ...workflow.steps.map((step) => ({
          ...step,
          data: {}
        })),
        {
          action: { value: 'review', label: 'Review' },
          isReviewStep: true
        }
      ];
    }
  });

  const onCancel = async () => {
    const reviewStepIndex = steps.length - 1;
    const isOnFirstStep = activeStep === 0;
    const isOnReviewStep = activeStep === reviewStepIndex;

    if (!isOnFirstStep && !isOnReviewStep) {
      const confirmCancel = confirm(
        'Canceling will discard and revert any changes made. Are you sure you want to cancel?'
      );

      if (!confirmCancel) {
        return;
      }

      const executedStepIndices = Object.keys(stepExecutionStatus)
        .filter((index) => stepExecutionStatus[index] === 'success')
        .map(Number)
        .sort((a, b) => b - a); // Reverse order

      for (const index of executedStepIndices) {
        const rollbackFn = stepRollbackFunctions[index];
        if (rollbackFn) {
          try {
            await rollbackFn();
          } catch (error) {
            console.error(`Failed to rollback step ${index}:`, error);
          }
        }
      }
    }

    navigate('/staff/workflows');
  };

  const onReturnToWorkflows = () => {
    navigate('/staff/workflows');
  };

  const onRerunWorkflow = () => {
    stepExecutionStatus = {};
    stepExecutionTimes = {};
    stepExecutionErrors = {};
    stepRequestLogs = {};
    activeStep = 0;

    if (workflow?.steps) {
      steps = [
        ...workflow.steps.map((step) => ({
          ...step,
          data: {}
        })),
        {
          action: { value: 'review', label: 'Review' },
          isReviewStep: true
        }
      ];
    }
  };

  const handleStepExecute = (index) => (executeFn) => {
    stepExecuteFunctions[index] = executeFn;
  };

  const handleStepRollback = (index) => (rollbackFn) => {
    stepRollbackFunctions[index] = rollbackFn;
  };

  const handleNext = async (index) => {
    const executeFn = stepExecuteFunctions[index];
    if (executeFn) {
      APITransport.clearRequestLog();
      const startTime = performance.now();
      try {
        await executeFn();

        const endTime = performance.now();
        const executionTime = endTime - startTime;

        stepRequestLogs[index] = APITransport.getRequestLog();

        stepExecutionStatus[index] = 'success';
        stepExecutionStatus = { ...stepExecutionStatus };

        stepExecutionTimes[index] = executionTime;
        stepExecutionTimes = { ...stepExecutionTimes };

        activeStep = index + 1;
      } catch (error) {
        console.error('Step execution failed:', error);

        const endTime = performance.now();
        const executionTime = endTime - startTime;

        stepRequestLogs[index] = APITransport.getRequestLog();

        stepExecutionStatus[index] = 'error';
        stepExecutionStatus = { ...stepExecutionStatus };

        stepExecutionTimes[index] = executionTime;
        stepExecutionTimes = { ...stepExecutionTimes };

        stepExecutionErrors[index] = { message: error.message, stack: error.stack };
        stepExecutionErrors = { ...stepExecutionErrors };
      }
    } else {
      activeStep = index + 1;
    }
  };

  const getStepInfo = (step) => {
    const actionValue = typeof step.action === 'string' ? step.action : step.action.value;
    const info = getWorkflowActionByValue(actionValue);
    return info || step.action;
  };

  const getDebugReport = (index) => {
    const step = steps[index];
    const stepInfo = getStepInfo(step);
    const actionValue = typeof step.action === 'string' ? step.action : step.action.value;

    const allStepsSummary = steps
      .filter((s) => !s.isReviewStep)
      .map((s, i) => {
        const info = getStepInfo(s);
        const status = stepExecutionStatus[i] || 'not executed';
        const errMsg = stepExecutionErrors[i]?.message;
        return `Step ${i + 1}: ${info?.label || 'Unknown'} (${typeof s.action === 'string' ? s.action : s.action.value}) — ${status}${errMsg ? ` — Error: ${errMsg}` : ''}`;
      })
      .join('\n');

    return {
      title: 'WORKFLOW STEP DEBUG REPORT',
      filename: `debug-step-${index + 1}-${actionValue}`,
      error: stepExecutionErrors[index],
      executionTime: stepExecutionTimes[index],
      requestLogs: stepRequestLogs[index] || [],
      details: {
        Workflow: workflow?.name || 'Unknown',
        'Total Steps': steps.length - 1,
        'Step Number': index + 1,
        Action: stepInfo?.label || 'Unknown',
        'Action Value': actionValue,
        Category: stepInfo?.categoryLabel || 'N/A',
        Section: stepInfo?.itemLabel || 'N/A',
        Description: stepInfo?.description || 'N/A'
      }
    };
  };
</script>

<div id="run-workflow" class="page">
  <div class="page-header">
    <Container fluid>
      <Row>
        <Col md="12">
          <Breadcrumb>
            <BreadcrumbItem>
              <a href="{basepath}/staff" use:link>{$format('label.STAFF_TOOLS')}</a>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <a href="{basepath}/staff/workflows" use:link>Workflows</a>
            </BreadcrumbItem>
            <BreadcrumbItem active>Run Workflow</BreadcrumbItem>
          </Breadcrumb>
        </Col>
      </Row>
      <Row class="flex-align--center">
        <Col xs="6" sm="6" md="8">
          <h1 class="page-title">{workflow ? workflow.name : ''}</h1>
        </Col>
        <Col xs="6" sm="6" md="4" class="text-right">
          <Button class="hide--mobile me-2" color="secondary" on:click={onCancel}>Cancel</Button>
        </Col>
      </Row>
    </Container>
  </div>
  <div class="page-content">
    <Container fluid>
      <Row>
        <Col md="12">
          {#if workflow && steps.length > 0}
            <div class="workflow-steps">
              {#each steps as step, index}
                {@const stepInfo = getStepInfo(step)}
                {#if stepInfo}
                  <Step
                    active={activeStep === index}
                    disabled={activeStep !== index}
                    summary={stepInfo.categoryLabel
                      ? stepInfo.categoryLabel + ' › ' + stepInfo.itemLabel + ' › ' + stepInfo.label
                      : stepInfo.itemLabel}
                    title="Step {index + 1}: {stepInfo.label}"
                    nextDisabled={stepNextDisabled[index]}
                    onNext={index < steps.length - 1 ? () => handleNext(index) : undefined}
                    debugReport={stepExecutionStatus[index] === 'error' ? getDebugReport(index) : undefined}
                    nextLabel={stepInfo.label}
                    executionStatus={stepExecutionStatus[index]}
                  >
                    {#if step.isReviewStep}
                      <ReviewStep
                        steps={steps.slice(0, -1)}
                        {stepExecutionStatus}
                        {stepExecutionTimes}
                        {stepExecutionErrors}
                        onRerun={onRerunWorkflow}
                        onReturn={onReturnToWorkflows}
                      />
                    {:else}
                      <StepContent
                        bind:step
                        bind:nextDisabled={stepNextDisabled[index]}
                        onExecute={handleStepExecute(index)}
                        onRollback={handleStepRollback(index)}
                      />
                    {/if}
                  </Step>
                {/if}
              {/each}
            </div>
          {:else if workflow}
            <p>Loading workflow steps...</p>
          {:else}
            <p>Workflow not found</p>
          {/if}
        </Col>
      </Row>
    </Container>
  </div>
</div>

<style lang="scss" src="./run.scss"></style>
